/*
 * ManageMenu — the navigation menu builder.
 *
 *   Card 1  Menu Design     the builder's own style panel (designPanel prop)
 *   Card 2  Menu structure  the Custom Menu toggle, the item tree, and an
 *                           "Add menu items" button that opens the picker
 *
 * Self-contained: zero references to ui.css, MasterMenu or PagesAccordion. All
 * styles are inline in the <style>{MM_STYLES}</style> block, scoped `mm-`.
 *
 *   USE_NEW_MENU below is the condition-based switch. Import it from anywhere
 *   and gate the render on it:
 *
 *     import ManageMenu, { USE_NEW_MENU } from "./ManageMenu";
 *     if (USE_NEW_MENU && open) return <ManageMenu open onClose={...} />;
 *
 * Data model (flat, ordered, per WordPress):
 *   items = [{ id, label, url, depth }, ...]
 *   depth 0..MAX_DEPTH  (MAX_DEPTH=4  =>  5 levels)
 *   A "block" = an item plus contiguous following items of greater depth.
 *   normalize() enforces: depth[0]===0 and depth[i] <= depth[i-1]+1.
 */

import React, { useEffect, useRef, useState } from "react";
import { createPortal, render } from "react-dom";
import app from "./app";
import { countryCode } from "./utility";
// Shared with the published renderer (getLinkURL) so the picker rejects exactly
// what the live site refuses to paint — one definition, checked at both ends.
import { isUnsafeHref } from "../preview/utility";
// The builder's own delete confirm, styled by ui.css and used the same way in
// editor.js and helpers.js. This is a circular import — helpers.js imports this
// file — which resolves because DeleteModal is only read inside removeItem,
// long after both modules have finished evaluating. Do not move the reference
// to module scope.
import { DeleteModal } from "./helpers";

/*
 * The editor app is loaded inside a container (`#ai-editor` or similar), not
 * at the viewport root. `position: fixed` would anchor to the browser viewport
 * and can extend behind surrounding chrome. To stay *inside the container*,
 * we portal into it and use `position: absolute; inset: …` so the popup fits
 * the container exactly.
 */
const getMountNode = function(){
  if (typeof document === "undefined") return null;
  var host =
    document.getElementById("ai-editor") ||
    document.querySelector(".app-overlay") ||
    document.body;
  if (host && getComputedStyle(host).position === "static") {
    host.style.position = "relative";
  }
  return host;
};

// Flip to false to fall back to the legacy navigation panel wherever the caller
// checks this flag.
export const USE_NEW_MENU = true;

const MAX_DEPTH = 4;
const INDENT_PX = 30;

let _uid = 1;
const nid = function(){ return "mm" + (_uid++); };

// ─── Data helpers ────────────────────────────────────────────────────

const flattenTree = function(nested, depth, out){
  if (depth === undefined) depth = 0;
  if (out === undefined) out = [];
  if (!Array.isArray(nested)) return out;
  for (var i = 0; i < nested.length; i++) {
    var n = nested[i]; if (!n) continue;
    out.push({
      id: n.id || nid(),
      label: n.label || n.title || "",
      url: n.url || n.value || "",
      depth: Math.min(depth, MAX_DEPTH)
    });
    var children = n.children || n.data;
    if (Array.isArray(children) && children.length) flattenTree(children, depth + 1, out);
  }
  return out;
};

const buildTree = function(items){
  var roots = [], stack = [];
  for (var i = 0; i < items.length; i++) {
    var it = items[i];
    var node = { label: it.label, url: it.url, children: [] };
    if (it.depth === 0 || !stack[it.depth - 1]) {
      roots.push(node);
      stack[0] = node;
      stack.length = 1;
    } else {
      stack[it.depth - 1].children.push(node);
      stack[it.depth] = node;
      stack.length = it.depth + 1;
    }
  }
  return roots;
};

const normalize = function(items){
  for (var k = 0; k < items.length; k++) {
    if (k === 0) items[k].depth = 0;
    else items[k].depth = Math.max(0, Math.min(items[k].depth, items[k-1].depth + 1, MAX_DEPTH));
  }
  return items;
};

const blockRange = function(items, index){
  var base = items[index].depth;
  var end = index;
  for (var k = index + 1; k < items.length; k++) {
    if (items[k].depth > base) end = k;
    else break;
  }
  return [index, end];
};

// app.getLang returns the KEY when the pack has no entry for it, so a popup
// wired straight to it would show "menu_structure" to a user whenever the
// server-side pack is behind this file. The pack is not bundled — it lives in
// app.getConfig().languageSettings, uploaded by the Save button on
// /language-settings — so that gap is normal, not exceptional.
//
// t() closes it: translate when the pack knows the key, fall back to the
// English we already ship when it does not. Never shows a raw key.
const t = function(key, fallback){
  var v = app.getLang(key);
  return v === key ? fallback : v;
};

// Longest navigation label an author can type, in characters. Applied with
// maxLength on every box that writes item.label, so the browser stops the
// keystroke and also trims a paste — there is no error state to explain.
//
// Only covers typed labels. A label taken from a picked page or anchor is
// whatever that page or block is called and can still arrive longer; editing it
// afterwards goes through a capped box.
const LABEL_MAX = 50;

// Tabs for the "Add menu items" popup. Order, labels and lang keys mirror the
// legacy LINK EDITOR (MenuModel in helpers.js) so the two editors read the same:
// a left sidebar of tabs, same wording, same sequence. Labels go through
// app.getLang, which falls back to the key itself when a language pack is
// missing — so the keys are the ones the pack already ships.
const ADD_TABS = [
  { id: "pages",  lang: "internalPage" },
  { id: "anchor", lang: "anchor_links" },
  { id: "custom", lang: "web_address" },
  { id: "email",  lang: "email" },
  { id: "phone",  lang: "phone" },
];

// Which picker tab owns an existing item, from its stored value. Internal pages
// return null — they have no fields to re-fill, you replace one by picking again.
const tabForValue = function(url){
  const u = String(url || "");
  if (/^mailto:/i.test(u)) return "email";
  if (/^tel:/i.test(u))    return "phone";
  if (/^https?:/i.test(u)) return "custom";
  if (u.indexOf("#") !== -1) return "anchor";
  return null;
};

// mailto:to?subject=…&body=… -> its three parts, percent-decoding included.
const parseMailto = function(url){
  const rest = String(url || "").replace(/^mailto:/i, "");
  const cut = rest.indexOf("?");
  const to = cut === -1 ? rest : rest.slice(0, cut);
  const out = { to: to, subject: "", body: "" };
  if (cut !== -1) {
    rest.slice(cut + 1).split("&").forEach(function(pair){
      const eq = pair.indexOf("=");
      if (eq === -1) return;
      const k = pair.slice(0, eq).toLowerCase();
      let v = pair.slice(eq + 1);
      try { v = decodeURIComponent(v.replace(/\+/g, " ")); } catch (_) {}
      if (k === "subject") out.subject = v;
      if (k === "body")    out.body = v;
    });
  }
  return out;
};

// tel:+919876543210 -> the countryCode row index and the bare number. Longest
// dial code wins, so +91 is preferred over +9 when both would prefix-match.
const parseTel = function(url){
  const digits = String(url || "").replace(/^tel:/i, "");
  let idx = 0, best = 0;
  for (let i = 0; i < countryCode.length; i++) {
    const dial = countryCode[i].dialCode;
    if (!dial || dial === "none") continue;
    if (digits.indexOf(dial) === 0 && dial.length > best) { best = dial.length; idx = i; }
  }
  return { idx: String(idx), number: best ? digits.slice(best) : digits };
};

const DEFAULT_PAGES = [
  { label: "Home",     url: "/" },
  { label: "About",    url: "/about" },
  { label: "Services", url: "/services" },
  { label: "Blog",     url: "/blog" },
  { label: "Contact",  url: "/contact" },
];

// Load pages from localStorage.featurePages (same shape MasterMenu uses)
// and normalize to { id, label, url, icon, children[] }.
const loadFeaturePages = function(){
  try {
    var raw = localStorage.getItem("featurePages");
    if (!raw) return null;
    var arr = JSON.parse(raw);
    if (!Array.isArray(arr) || !arr.length) return null;
    // Wrapped, not `arr.map(normalizePage)`: map passes the INDEX as the second
    // argument, which normalizePage reads as parentPath — so every top-level
    // page came out with its array position glued on the front ("1/store",
    // "8/articles"). Nothing surfaced it because pageId, not url, is what gets
    // stored when you pick a page; the wrong urls only broke the reverse lookup
    // that maps a path back to a pageId.
    return arr.map(function(p){ return normalizePage(p); });
  } catch (_) { return null; }
};
const normalizePage = function(p, parentPath){
  // pageId keeps its underscores ("aiarticle_1774343495231_72") because that is
  // what a menu item stores as its value; id is the stripped form, used only as a
  // local key for the checkbox map and React lists.
  var pageId = String(p.pageId || p.id || "");
  var id = pageId.replace(/_/g, "");
  var slug = p.pageUrl || p.slug || id;
  var isHome = (p.pageIdentifier === "homenew" || p.pageIdentifier === "home" || slug === "PAGE_IDENTIFIER_HOME");
  var url = isHome ? "/" : (parentPath ? parentPath + "/" + slug : "/" + slug);
  var kids = Array.isArray(p.folderPages || p.children) ? (p.folderPages || p.children) : [];
  return {
    id: id,
    pageId: pageId,
    // pageNewid first, matching every other place a page title is displayed
    // (src/preview/helpers.js nav links, src/preview/header.js): it holds the
    // author's renamed label, while pageName is the original. Using pageName
    // here showed the pre-rename name in the menu.
    label: p.pageNewid || p.pageName || p.label || id,
    url: url,
    identifier: p.pageIdentifier || "",
    // Drives the Anchors filter: only AI-built pages have blocks to anchor to.
    isAIPage: p.isAIPage,
    // Match the LINK EDITOR's Internal Page tab: fontello class from data.
    // pageIcon is the class name from featurePages (e.g. "appyslim-home").
    // Falls back to my SVG mapping if absent.
    pageIcon: p.pageIcon || "",
    icon: iconForIdentifier(p.pageIdentifier),
    // Pass the accumulated `url`, not "/" + slug: the latter drops every
    // ancestor above the immediate parent, so a page two folders deep came out
    // as "/inner/deep" instead of "/outer/inner/deep". AnchorBlocks builds the
    // full path, so the two lists disagreed and every pageId lookup missed.
    children: kids.map(function(c){ return normalizePage(c, isHome ? "" : url); })
  };
};

// ── Legacy menu migration ────────────────────────────────────────────────────
// Menus saved before this picker use an older value vocabulary. Three of the
// differences are not cosmetic — they are already broken on the live site:
//
//   "+93-454545343434"       a bare phone, the format MenuModel wrote. getLinkURL
//                            matches no scheme, does not start with "/" or "#",
//                            and finds no page by that id, so it returns null and
//                            React drops the href — a dead link. tabForValue also
//                            returns null, so no Edit button renders and the item
//                            cannot be repaired from the popup either.
//   "/PAGE_IDENTIFIER_HOME"  the raw home placeholder. getLinkURL passes anything
//                            starting with "/" through verbatim, so this ships to
//                            visitors as a 404 instead of the home page.
//   id: 1785942973311        a numeric id. Rows carry data-id and getAttribute
//                            returns a string, so endDrag's `it.id === d.beforeId`
//                            never matches and dragging that row silently sends it
//                            to the bottom of the menu.
//
// Page paths ("/store") do still resolve, but only until someone renames the
// page — storing the pageId is exactly what makes a menu entry survive a rename.
// Those are converted only on an unambiguous match; a path we cannot resolve is
// left byte-for-byte as it was, so migration can never invent a broken link.
//
// Idempotent: every branch is a no-op on already-migrated values, so this is safe
// to run on every load and needs no schema-version flag to maintain.
//
// CURRENTLY NOT WIRED IN. helpers.js passes data.navigationMenu straight to
// initialItems; the single call that used this was unwired on purpose and the
// one-line edit to restore it is spelled out at that call site. Kept here
// deliberately, not dead by accident — do not delete it.
export const migrateLegacyMenu = function(list, pages){
  var walk = function(nodes){
    if (!Array.isArray(nodes)) return [];
    return nodes.map(function(n){
      if (!n) return n;
      var out = Object.assign({}, n);
      // The legacy id IS the pageId with its underscores stripped - see
      // normalizePage above, which does pageId.replace(/_/g, "") - so the new
      // value can be read straight off the id without the page tree.
      //
      // String() because the id is not always one: saved menus carry numeric
      // ids (1785942973311), and calling .indexOf on a number throws.
      // Only rewrite when the id actually looks like a page identifier. A saved
      // menu also carries ids that are bare timestamps ("1785940883454", from
      // the earlier picker) or no id at all, and deriving a value from those
      // produced a link to nothing: the timestamp matched no page, and a missing
      // id wrote an empty value, dropping the href entirely. Both destroyed the
      // stored path with no way back, on read, silently.
      //
      // A page identifier always starts with letters - "hyperstore1766...",
      // "folder1768...", "about1766..." - because normalizePage builds it from
      // the page's identifier plus digits. All-digits, or empty, means we cannot
      // resolve it, so the stored value is left exactly as it was.
      if(out.type === "page") {
        var pid = String(out.id === undefined || out.id === null ? "" : out.id);
        var looksLikePageId = /^[a-zA-Z]/.test(pid);
        if(looksLikePageId) {
          if(pid.indexOf("folder") === 0 && pid.split("_").length > 1) {
            out.value = pid.split("_")[1];
          }
          else {
            out.value = pid;
          }
        }
        // else: keep out.value untouched - a path still resolves through
        // getLinkURL, where a timestamp or an empty string resolves to nothing.
      }
      // Reads `value`, not `url`: the stored shape is {title, type, value, id,
      // data} and there is no url key, so out.url was undefined and .replace
      // threw on every anchor item.
      if(out.type === "anchorLinks") {
        out.type = "anchor";
        out.value = String(n.value || n.url || "").replace('/', "");
      }
      if(out.type === "link") {
        out.type = "external";
      }
      out.id = app.menuItemId();
      
      out.data = Array.isArray(n.data) ? walk(n.data) : [];
      return out;
    });
  };
  
  var needsMigration = function(nodes){
    if (!Array.isArray(nodes) || !nodes.length) return false;
    var first = nodes[0];
    if (!first || first.id === undefined || first.id === null) return true;
    return String(first.id).length !== 8;
  };

  var need = needsMigration(list);
  if(need) {
    return walk(list);
  }
  return list;
};

// Icon per page identifier — inline SVG paths (24x24 viewBox).
const iconForIdentifier = function(id){
  var ID = String(id || "").toLowerCase();
  if (ID.indexOf("home") === 0) return "home";
  if (ID.indexOf("about") === 0 || ID === "aiarticle") return "person";
  if (ID.indexOf("real") === 0) return "building";
  if (ID.indexOf("contact") === 0 || ID.indexOf("phone") === 0) return "phone";
  if (ID.indexOf("store") === 0 || ID === "hyperstore") return "store";
  if (ID.indexOf("forum") === 0 || ID.indexOf("chat") === 0) return "chat";
  if (ID === "folder") return "folder";
  if (ID.indexOf("blog") === 0 || ID.indexOf("article") === 0) return "doc";
  if (ID.indexOf("aifeature") === 0) return "star";
  return "doc";
};
const PageIcon = function({ name }) {
  const paths = {
    home:    "M3 12l9-9 9 9M5 10v10h4v-6h6v6h4V10",
    person:  "M12 12a4 4 0 100-8 4 4 0 000 8zM4 20v-1a6 6 0 0112 0v1",
    building:"M4 21V7l8-4 8 4v14M9 12h2M13 12h2M9 16h2M13 16h2",
    phone:   "M4 6a2 2 0 012-2h2l2 5-2 1a12 12 0 006 6l1-2 5 2v2a2 2 0 01-2 2A16 16 0 014 6z",
    store:   "M4 8h16l-1 12H5L4 8zM8 8V6a4 4 0 018 0v2",
    chat:    "M4 5h16v10H8l-4 4V5z",
    folder:  "M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V7z",
    doc:     "M6 3h9l5 5v13a1 1 0 01-1 1H6a1 1 0 01-1-1V4a1 1 0 011-1zM14 3v6h6",
    star:    "M12 3l2.5 6 6.5.5-5 4.5 1.5 6.5L12 17l-5.5 3.5L8 14 3 9.5 9.5 9z"
  };
  var d = paths[name] || paths.doc;
  return <svg className="mm-pg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d={d} /></svg>;
};

const DEFAULT_DESIGN = {
  textColor: "#111827",
  hoverColor: "#3899ED",
  activeColor: "#1d4ed8",
  backgroundColor: "#ffffff",
  fontSize: 14,
  fontWeight: "500",
  layout: "horizontal",
};

// ─── Main component ──────────────────────────────────────────────────

export default function ManageMenu({
  open,
  onClose,
  initialItems,   // nested tree OR flat-with-depth array
  pages,          // preset pages [{label,url}]; falls back to DEFAULT_PAGES
  initialDesign,      // { textColor, hoverColor, activeColor, backgroundColor, fontSize, fontWeight, layout }
  initialCustomMenu,  // bool — initial state of the Custom Menu toggle (default: true)
  onChange,           // ({items, tree, design, customMenu}) => void — fires on every mutation
  // The Menu Design column — the builder's own style panel (<TabComponent>),
  // passed in as an element because its ~18 props are wired to the caller's
  // update handlers. Required in practice: the only mount, helpers.js:3392,
  // always supplies it, and without it that card renders empty.
  designPanel,
}) {
  const [design] = useState({ ...DEFAULT_DESIGN, ...(initialDesign || {}) });
  const [customOn, setCustomOn] = useState(initialCustomMenu !== undefined ? !!initialCustomMenu : true);
  const [items, setItems] = useState(function(){
    if (Array.isArray(initialItems) && initialItems.length && initialItems[0].depth !== undefined) {
      return initialItems.map(function(v){ return { ...v, id: v.id || nid() }; });
    }
    return flattenTree(Array.isArray(initialItems) ? initialItems : []);
  });
  const [openIds, setOpenIds] = useState({});
  const [openPanel, setOpenPanel] = useState("pages");
  // Add-items drawer, opened from the button at the foot of the structure card.
  const [addOpen, setAddOpen] = useState(false);
  // Index of the item the popup is editing, or null when it is adding a new one.
  // Save writes back to this row instead of appending.
  const [editIndex, setEditIndex] = useState(null);
  // Bumped whenever a label changes from outside the accordion (i.e. the popup's
  // Save). The label box is uncontrolled — updateField mutates in place without
  // setItems so typing keeps focus — so React will not refresh it on re-render.
  // Keying the input on this remounts it, and only when the change came from
  // elsewhere; typing never bumps it.
  const [labelRev, setLabelRev] = useState(0);
  const [checkedPages, setCheckedPages] = useState({});
  const [customUrl, setCustomUrl] = useState("https://");
  const [customText, setCustomText] = useState("");
  // Index into countryCode, not the dial code itself — see the select below.
  const [phoneIdx, setPhoneIdx] = useState("0");
  const [phoneNum, setPhoneNum] = useState("");
  const [phoneText, setPhoneText] = useState("");
  const [emailAddr, setEmailAddr] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [emailText, setEmailText] = useState("");
  // Anchors: what the picker's "Select" last reported, held until Save. Every
  // tab commits on Save, so picking a section must not add the item outright.
  const [anchorPick, setAnchorPick] = useState(null);
  const [drag, setDrag] = useState(null);

  const dragRef = useRef(null);
  dragRef.current = drag;
  const listRef = useRef(null);

  // Auto-load pages from localStorage.featurePages if not provided as prop.
  const [autoPages, setAutoPages] = useState(function(){ return loadFeaturePages(); });
  useEffect(function(){
    if (!open || pages) return;
    if (autoPages && autoPages.length) return;
    setAutoPages(loadFeaturePages());
  }, [open, pages]);
  var availablePages;
  if (Array.isArray(pages) && pages.length) availablePages = pages;
  else if (autoPages && autoPages.length) availablePages = autoPages;
  else availablePages = DEFAULT_PAGES.map(function(p, i){ return { id: "d" + i, label: p.label, url: p.url, icon: iconForIdentifier(p.label.toLowerCase()), children: [] }; });

  // ── notify ────────────────────────────────────────────────────────
  const commit = function(next){
    normalize(next);
    setItems(next.slice());
    if (onChange) onChange({ items: next, tree: buildTree(next), design: design, customMenu: customOn });
  };
  const handleCustomToggle = function(v){
    setCustomOn(v);
    if (onChange) onChange({ items: items, tree: buildTree(items), design: design, customMenu: v });
  };

  // ── item mutations ────────────────────────────────────────────────
  // What every tab's Save goes through: appends a new row, or rewrites the one
  // the popup was opened from, keeping its id and depth so its children stay put.
  const saveItem = function(label, url){
    if (editIndex === null) { addItem(label, url); return; }
    var next = items.slice();
    if (next[editIndex]) next[editIndex] = { ...next[editIndex], label: label, url: url };
    commit(next);
    setLabelRev(function(n){ return n + 1; });
    setEditIndex(null);
  };
  const closeAdd = function(){
    setAddOpen(false);
    setEditIndex(null);
  };
  // Wipe every tab's fields. openEdit fills them from the item being edited, and
  // closing the popup without saving used to leave them filled — so the next
  // "Add menu items" opened holding the last edited item's values. Called on the
  // add path so a new item always starts blank, however the previous session ended.
  // phoneIdx is deliberately not reset: the chosen country carries between adds.
  const resetPickerFields = function(){
    setCustomUrl("https://"); setCustomText("");
    setEmailAddr(""); setEmailSubject(""); setEmailBody(""); setEmailText("");
    setPhoneNum(""); setPhoneText("");
    setAnchorPick(null);
    setCheckedPages({});
  };
  // Re-entry guard. resetPickerFields() below wipes every tab's fields, so a
  // second openAdd() while the popup is already up erases whatever the author
  // has typed so far. Reported as a menu label losing its leading characters —
  // "Redirect" stored as "edirect" — which is what a reset landing between the
  // first keystroke and the rest looks like: the "R" is dropped with the old
  // state and the remaining letters accumulate into the blank field.
  const openAdd = function(){
    if (addOpen) return;
    setEditIndex(null);
    resetPickerFields();
    setAddOpen(true);
  };
  // Open the popup on the tab that owns this item, with its fields filled in.
  const openEdit = function(idx){
    resetPickerFields();                 // never inherit the previous session
    const it = items[idx];
    const tab = it && tabForValue(it.url);
    if (!tab) return;
    if (tab === "email") {
      const m = parseMailto(it.url);
      setEmailAddr(m.to); setEmailSubject(m.subject); setEmailBody(m.body);
      setEmailText(it.label || "");
    } else if (tab === "phone") {
      const t = parseTel(it.url);
      setPhoneIdx(t.idx); setPhoneNum(t.number);
      setPhoneText(it.label || "");
    } else if (tab === "custom") {
      setCustomUrl(it.url); setCustomText(it.label || "");
    } else if (tab === "anchor") {
      setAnchorPick({ label: it.label || "", url: it.url });
    }
    setEditIndex(idx);
    setOpenPanel(tab);
    setAddOpen(true);
  };

  const addItem = function(label, url){
    var next = items.concat([{ id: nid(), label: label, url: url, depth: 0 }]);
    commit(next);
  };
  const addItems = function(newItems){
    if (!Array.isArray(newItems) || !newItems.length) return;
    // Preserve per-item depth so tree-selections (e.g., a folder with pages
    // inside) come across as menu → submenu. Cap at MAX_DEPTH to respect the
    // WordPress-style 5-level limit; normalize() will fix any invalid step
    // (e.g., depth jumping from 0 → 2 with no intermediate).
    var next = items.concat(newItems.map(function(x){
      var d = typeof x.depth === "number" ? x.depth : 0;
      if (d < 0) d = 0; if (d > MAX_DEPTH) d = MAX_DEPTH;
      return { id: nid(), label: x.label || "", url: x.url || "", depth: d };
    }));
    commit(next);
  };
  const removeItem = function(idx){
    var range = blockRange(items, idx);
    var count = range[1] - range[0] + 1;                  // block size (item + its nested children)
    var target = items[idx];
    var label = (target && target.label) || "this item";
    var msg = count > 1
      ? 'Are you sure you want to remove "' + label + '" and ' + (count - 1)
        + ' nested item' + (count > 2 ? 's' : '') + "?"
      : 'Are you sure you want to remove "' + label + '"?';
    // Same shape as the section delete in editor.js: mount DeleteModal on a
    // throwaway div and let it take itself down. It is styled by ui.css and
    // carries the Cancel button window.confirm could not be given.
    //
    // ai-popup-layer because DeleteModal renders bootstrap's .modal, whose
    // z-index of 1055 puts it *behind* this popup (backdrop 9998, popup 9999,
    // add-items overlay 10001). That is the same collision the image and link
    // pickers hit, and MM_STYLES already lifts anything inside .ai-popup-layer
    // to 10050 — so wearing the class is the whole fix. A bare .modal rule
    // would be far too blunt; see the comment on that rule.
    var div = document.createElement("div");
    div.className = "ai-popup-layer";
    document.body.appendChild(div);
    render(<DeleteModal title={app.getLang("remove")} summary={msg} callback={function(){
      // Recompute from the current items rather than reusing the range measured
      // when Remove was pressed — the modal stays up across renders, and a stale
      // range would splice the wrong rows.
      var live = blockRange(items, idx);
      commit(items.slice(0, live[0]).concat(items.slice(live[1]+1)));
    }}/>, div);
  };
  const moveBy = function(idx, delta){
    var range = blockRange(items, idx);
    var block = items.slice(range[0], range[1]+1);
    var rest = items.slice(0, range[0]).concat(items.slice(range[1]+1));
    var insertAt;
    if (delta < 0) {
      if (range[0] === 0) return;
      insertAt = range[0] - 1;
      while (insertAt > 0 && rest[insertAt] && rest[insertAt].depth > block[0].depth) insertAt--;
    } else {
      var nextIdx = range[1] + 1;
      if (nextIdx >= items.length) return;
      var nextRange = blockRange(items, nextIdx);
      insertAt = nextRange[1] + 1 - block.length;
    }
    if (insertAt < 0) insertAt = 0;
    commit(rest.slice(0, insertAt).concat(block).concat(rest.slice(insertAt)));
  };
  const reIndent = function(idx, delta){
    var range = blockRange(items, idx);
    var maxAllowed = idx === 0 ? 0 : Math.min(items[idx-1].depth + 1, MAX_DEPTH);
    var target = Math.max(0, Math.min(items[idx].depth + delta, maxAllowed, MAX_DEPTH));
    var applied = target - items[idx].depth;
    if (applied === 0) return;
    var next = items.slice();
    for (var i = range[0]; i <= range[1]; i++) {
      next[i] = { ...next[i], depth: Math.max(0, next[i].depth + applied) };
    }
    commit(next);
  };
  const toggleEdit = function(id){
    setOpenIds(function(prev){ return { ...prev, [id]: !prev[id] }; });
  };
  const collapseAll = function(){ setOpenIds({}); };

  // Inline label/URL edit — mutate in place, avoid re-render to keep focus.
  //
  // Called once per finished edit, from the field's blur handler. It used to run
  // on every keystroke and emit each time, pushing a fresh snapshot up the chain
  // character by character. Because the editor persists whatever state.data
  // holds when some other action triggers a save, a save landing mid-word stored
  // a prefix of the label - "New Feature Page For custom" arriving as "New
  // Feature Page For cu". Reading the box once, on blur, means no partial value
  // ever exists for a save to capture.
  const updateField = function(idx, field, value){
    items[idx][field] = value;
    // Update the visible title span if present.
    var node = listRef.current;
    if (node && field === "label") {
      var span = node.querySelector('[data-index="'+idx+'"] .mm-item-title');
      if (span) span.textContent = value || "(no label)";
    }
  };

  // Propagate the finished edit, on blur. items[] is already current - every
  // keystroke wrote to it above - so any other emitter that fires first
  // (commit, handleCustomToggle) still carries the typed
  // value; this only decides when the change is pushed upward.
  //
  // Must carry design and customMenu like every other emitter. The caller does
  // an unconditional `data.properties.customMenu = next.customMenu`, so
  // omitting the field wrote `undefined` over it — and the published header
  // gates on `customMenu === true`, so one keystroke in a Navigation label
  // silently dropped the whole custom menu back to the auto-generated page list.
  const commitField = function(){
    if (onChange) onChange({ items: items.slice(), tree: buildTree(items), design: design, customMenu: customOn });
  };

  // ── drag & drop ───────────────────────────────────────────────────
  const onDragMove = function(e){
    var d = dragRef.current; if (!d) return;
    var node = listRef.current; if (!node) return;
    var rows = Array.prototype.slice.call(node.querySelectorAll(".mm-item"));

    // Skip dragging block
    var draggingIds = {};
    d.ids.forEach(function(id){ draggingIds[id] = true; });

    var beforeId = null;
    for (var r = 0; r < rows.length; r++) {
      var rowId = rows[r].getAttribute("data-id");
      if (draggingIds[rowId]) continue;
      var rect = rows[r].getBoundingClientRect();
      if (e.clientY < rect.top + rect.height/2) { beforeId = rowId; break; }
    }

    // Compute target depth from horizontal delta
    var remaining = items.filter(function(it){ return !draggingIds[it.id]; });
    var pos = beforeId === null ? remaining.length : remaining.findIndex(function(it){ return it.id === beforeId; });
    var prev = remaining[pos - 1];
    var maxDepth = prev ? Math.min(prev.depth + 1, MAX_DEPTH) : 0;
    var want = d.baseDepth + Math.round((e.clientX - d.startX) / INDENT_PX);
    if (want < 0) want = 0;
    if (want > maxDepth) want = maxDepth;

    setDrag({ ...d, curX: e.clientX, curY: e.clientY, beforeId: beforeId, depth: want });
  };

  const endDrag = function(){
    window.removeEventListener("pointermove", onDragMove);
    var d = dragRef.current;
    if (!d) return;
    var block = [];
    d.ids.forEach(function(id){
      var found = items.find(function(it){ return it.id === id; });
      if (found) block.push({ ...found });
    });
    var rest = items.filter(function(it){ return d.ids.indexOf(it.id) === -1; });
    var pos = d.beforeId === null ? rest.length : rest.findIndex(function(it){ return it.id === d.beforeId; });
    if (pos < 0) pos = rest.length;
    var delta = d.depth - block[0].depth;
    block.forEach(function(b){ b.depth = Math.max(0, b.depth + delta); });
    setDrag(null);
    commit(rest.slice(0, pos).concat(block).concat(rest.slice(pos)));
  };

  const startDrag = function(e, idx){
    e.preventDefault();
    var range = blockRange(items, idx);
    var ids = items.slice(range[0], range[1]+1).map(function(x){ return x.id; });
    setDrag({
      ids: ids,
      baseDepth: items[range[0]].depth,
      startX: e.clientX, startY: e.clientY,
      curX: e.clientX, curY: e.clientY,
      beforeId: undefined,
      depth: items[range[0]].depth,
      label: items[range[0]].label
    });
    window.addEventListener("pointermove", onDragMove);
    window.addEventListener("pointerup", endDrag, { once: true });
  };

  // ── accordion helpers ─────────────────────────────────────────────

  // Walk the page tree, collecting checked nodes with a depth relative to the
  // top-most checked ancestor. This is how "select a folder → its pages come
  // in as submenu items" gets modeled — the folder becomes the depth-0 root
  // and its descendants are depth 1, 2, … (capped at MAX_DEPTH).
  const collectChecked = function(nodes, checkedMap, urlOf){
    var out = [];
    var walk = function(list, rootDepth, ancestorChecked){
      list.forEach(function(p){
        var thisChecked = !!checkedMap[p.id];
        var isRoot = thisChecked && !ancestorChecked;
        var thisDepth = isRoot ? 0 : (thisChecked ? rootDepth + 1 : rootDepth);
        if (thisChecked) {
          out.push({ label: p.label, url: urlOf(p), depth: thisDepth });
        }
        if (p.children && p.children.length) {
          walk(
            p.children,
            thisChecked ? thisDepth : rootDepth,
            ancestorChecked || thisChecked
          );
        }
      });
    };
    walk(nodes, -1, false);
    return out;
  };

  // Internal pages and their anchors are stored by pageId, not by path, so the
  // link survives a page URL rename. getLinkURL resolves it when rendering.
  // Falls back to the path for the DEFAULT_PAGES preset, which has no pageId.
  const doAddPages = function(){
    var toAdd = collectChecked(availablePages, checkedPages, function(p){ return p.pageId || p.url; });
    if (!toAdd.length) return;
    addItems(toAdd);
    setCheckedPages({});
    closeAdd();
  };

  // Seed an empty custom menu with every page, so switching the feature on does
  // not drop the author into a blank list they have to rebuild by hand. This is
  // what MasterMenu's cloneMenu did for the old popup; the same rule applies —
  // it runs ONLY when the menu has no items, so an existing menu is never
  // touched, reordered or duplicated.
  //
  // Driven by an effect rather than handleCustomToggle because the toggle
  // defaults to on (see initialCustomMenu above): a header whose customMenu was
  // never saved opens with the switch already on and fires no change event, so
  // a handler-only version would miss the first-run case it exists for. The
  // effect covers both entry points — opening with it on, and flipping it on.
  //
  // It also has to be an effect for correctness: commit() emits the CURRENT
  // customOn, and inside handleCustomToggle that closure still holds the old
  // value, so seeding from there would write customMenu:false back over the
  // toggle the author just switched on.
  const seededRef = useRef(false);
  useEffect(function(){
    if (!open) { seededRef.current = false; return; }
    if (!customOn || seededRef.current) return;
    // Already has data — nothing to seed, and never overwrite what is there.
    if (items.length) return;
    // Only seed from a real page list. availablePages falls back to the
    // DEFAULT_PAGES preset, which has no pageId and would seed placeholder rows
    // pointing at pages that may not exist.
    var real = (Array.isArray(pages) && pages.length) ? pages
             : ((autoPages && autoPages.length) ? autoPages : null);
    if (!real) return;
    var checked = {};
    var mark = function(list){
      list.forEach(function(p){
        checked[p.id] = true;
        if (p.children && p.children.length) mark(p.children);
      });
    };
    mark(real);
    // Reuses the add-pages path, so folders arrive as submenus, depth is capped
    // at MAX_DEPTH, and each value is the pageId that survives a page rename.
    var seeded = collectChecked(real, checked, function(p){ return p.pageId || p.url; });
    if (!seeded.length) return;
    seededRef.current = true;
    addItems(seeded);
  }, [open, customOn, items.length, pages, autoPages]);

  // Stored as "<pageId>#<viewId>". The legacy LINK EDITOR stored
  // "<pageUrl>#<blockId>", but pageId is what the rest of the menu now stores,
  // and getLinkURL splits on the first "#" either way. viewId is the same value
  // the renderer emits as widget-id, so the browser finds the section by id.
  // Bridge for AnchorBlocks, the anchor picker below. It reports
  // "<pageUrl>#<blockId>", a path — rewrite the path half to the page's pageId so
  // anchors match everything else the menu stores and survive a URL rename.
  // Falls back to the raw value if no page matches, which getLinkURL still
  // resolves because a leading "/" passes straight through.
  //
  // The label is the page's own title, taken from the row that was clicked in
  // the picker — AnchorBlocks passes it as the second argument. Deriving it by
  // matching the reported path back against availablePages (a different list,
  // built from localStorage.featurePages) missed whenever the two disagreed on
  // a slug, and the item then landed in the menu labelled with a raw path.
  const onLegacyAnchorValue = function(value, picked){
    const raw = String(value || "");
    if (!raw) return;
    const cut = raw.indexOf("#");
    const path = cut === -1 ? raw : raw.slice(0, cut);
    const frag = cut === -1 ? ""  : raw.slice(cut);

    const findByUrl = function(list){
      for (let i = 0; i < (list || []).length; i++) {
        const p = list[i];
        if (!p) continue;
        if (p.url === path) return p;
        const hit = findByUrl(p.children);
        if (hit) return hit;
      }
      return null;
    };
    const page = findByUrl(availablePages);

    const label = (picked && picked.pageName) || (page && page.label) || path;
    setAnchorPick({
      label: label,
      url: (page && page.pageId ? page.pageId : path) + frag
    });

    // Confirm the pick at the moment it happens. AnchorBlocks gives no feedback
    // of its own once the click is over, and the "Selected:" line in the panel
    // foot is easy to miss while you are still looking at the page tree — so
    // picking a section felt like nothing had happened. Same toast the rest of
    // the popup uses, so it reads as ordinary editor feedback.
    //
    // Escaped first: app.toast builds its markup with innerHTML and this label
    // is a page title, i.e. author-controlled. Every other caller passes a
    // static string, so the sink has not needed escaping before now. Doing it
    // here keeps the fix local rather than changing app.toast for everyone.
    var safeLabel = String(label).replace(/[&<>"']/g, function(c){
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
    app.toast("Anchor selected: " + safeLabel + ". Click Save to add it to the menu.");
  };
  const doAddAnchor = function(){
    if (!anchorPick) return;
    saveItem(anchorPick.label, anchorPick.url);
    setAnchorPick(null);
    closeAdd();
  };

  const doAddCustom = function(){
    var u = customUrl.trim();
    if (!u) return;
    // The Save button is already disabled for these, but guard here too: the
    // button's disabled state is a UI affordance, this is the one the value has
    // to pass to be stored.
    if (isUnsafeHref(u)) { app.toast(app.getLang("validUrl")); return; }
    var l = customText.trim() || u;
    saveItem(l, u);
    setCustomUrl("https://"); setCustomText("");
    closeAdd();
  };
  // "tel:<dial><digits>", NOT the LINK EDITOR's bare "+91-1234567890". The live
  // header runs every value through getLinkURL (src/preview/helpers.js), which
  // passes tel: straight through but has no branch for a bare number — that
  // falls to the page lookup, misses, and returns null, i.e. a dead link.
  const doAddPhone = function(){
    var n = phoneNum.replace(/[^0-9]/g, ""); if (!n) return;
    var row = countryCode[parseInt(phoneIdx, 10)] || {};
    var dial = (!row.dialCode || row.dialCode === "none") ? "" : row.dialCode;
    var l = phoneText.trim() || (dial ? dial + " " + phoneNum.trim() : phoneNum.trim());
    saveItem(l, "tel:" + dial + n);
    // The country is deliberately kept: clearing it meant the second number you
    // added silently lost its dial code while the field still looked filled in.
    setPhoneNum(""); setPhoneText("");
    closeAdd();
  };
  // Subject and body are appended only when filled, and percent-encoded — the
  // LINK EDITOR always writes "?subject=&body=" raw, so an "&" in a subject
  // silently truncates the rest of the mailto.
  const doAddEmail = function(){
    var e = emailAddr.trim(); if (!e) return;
    var q = [];
    if (emailSubject.trim()) q.push("subject=" + encodeURIComponent(emailSubject.trim()));
    if (emailBody.trim())    q.push("body="    + encodeURIComponent(emailBody.trim()));
    var l = emailText.trim() || e;
    saveItem(l, "mailto:" + e + (q.length ? "?" + q.join("&") : ""));
    setEmailAddr(""); setEmailSubject(""); setEmailBody(""); setEmailText("");
    closeAdd();
  };

  // Escape closes the popup
  useEffect(function(){
    if (!open) return;
    var onKey = function(e){ if (e.key === "Escape" && onClose) onClose(); };
    window.addEventListener("keydown", onKey);
    return function(){ window.removeEventListener("keydown", onKey); };
  }, [open, onClose]);

  // Publishing closes the popup as well, so the author is not left staring at
  // the menu editor over a page that has already gone out.
  //
  // Delegated on document rather than bound to the button. Save belongs to the
  // host page — nothing in this bundle renders .publish-btn, every reference to
  // it is a querySelector — so it can be absent when this runs, or replaced
  // later. A listener on document catches the click either way, because it
  // bubbles. Binding to the node would silently stop working the first time the
  // host re-rendered it.
  //
  // closest() rather than matching e.target, since the click usually lands on a
  // span or icon inside the button. It also picks up the .startTrialModal
  // variant, which carries the same publish-btn class.
  //
  // ".mm-header .mm-close", not ".mm-close" on its own: two buttons carry that
  // class — the popup's, and the Add panel's, which only calls closeAdd. A bare
  // selector returns whichever comes first in the document and would quietly
  // start closing the wrong one if this markup were ever reordered.
  useEffect(function(){
    if (!open) return;
    var onPublishClick = function(e){
      var t = e.target;
      if (t && t.closest && t.closest(".btn.btn-success.publish-btn")) {
        document.querySelector(".mm-header .mm-close")?.click();
      }
    };
    document.addEventListener("click", onPublishClick);
    return function(){ document.removeEventListener("click", onPublishClick); };
  }, [open]);

  if (!open) return null;

  const tree = buildTree(items);
  const draggingSet = drag ? drag.ids.reduce(function(a, id){ a[id]=true; return a; }, {}) : {};

  const mountNode = getMountNode();
  if (!mountNode) return null;

  return createPortal(<>
    <style>{MM_STYLES}</style>
    <div className="mm-backdrop" onClick={onClose} />
    <div className="mm-popup" role="dialog" aria-modal="true">

      <header className="mm-header">
        <h1 className="mm-title">{t("manage_menu", "Manage Menu")}</h1>
        <label className="mm-toggle">
          <span>{t("custom_menu", "Custom Menu")}</span>
          <input type="checkbox" checked={customOn} onChange={function(e){ handleCustomToggle(e.target.checked); }} />
          <span className={"mm-toggle-switch" + (customOn ? " mm-on" : "")} />
        </label>
        <button type="button" className="mm-close" onClick={onClose} aria-label={t("close", "Close")}>×</button>
      </header>

      <div className="mm-body mm-body-2col">

        {/* Card 1 — Menu Design */}
        <div className="mm-col">
          <section className="mm-card">
            <div className="mm-card-head">{t("menu_design", "Menu Design")}</div>
            <div className="mm-card-body">
              {/* The builder's own style panel, so colours, typography and
                  spacing behave identically to every other element. There is no
                  local fallback: a hand-rolled one existed here and never
                  rendered, because the sole caller always passes designPanel. */}
              {designPanel ? <div className="mm-design-legacy">{designPanel}</div> : null}
            </div>
          </section>
        </div>

        {/* Card 2 — Menu structure */}
        <div className="mm-col">

          <section className={"mm-card" + (customOn ? "" : " mm-card-disabled")}>
            <div className="mm-card-head">
              <span>{t("menu_structure", "Menu structure")}</span>
              <button type="button" className="mm-btn mm-btn-sm mm-btn-ghost" onClick={collapseAll} disabled={!customOn}>{t("close_all", "Close all")}</button>
            </div>
            <div className="mm-card-body mm-card-list">
              <ul className="mm-menu-list" ref={listRef}>
                {items.length === 0 ? (
                  <li className="mm-empty">No items yet — add a page or custom link on the left.</li>
                ) : items.map(function(it, i){
                  var isEditing = !!openIds[it.id];
                  var isDrag = !!draggingSet[it.id];
                  var maxAllowed = i === 0 ? 0 : Math.min(items[i-1].depth + 1, MAX_DEPTH);
                  return <li key={it.id}
                             data-id={it.id}
                             data-index={i}
                             className={"mm-item" + (isEditing ? " mm-editing" : "") + (isDrag ? " mm-dragging" : "")}
                             style={{ "--depth": it.depth }}>
                    <div className="mm-item-row" style={{ marginLeft: (it.depth * INDENT_PX) + "px" }}>
                      <span className="mm-handle" onPointerDown={function(e){ startDrag(e, i); }} title="Drag to move" aria-label={t("drag_handle", "Drag handle")}>
                        <svg className="mm-svg" viewBox="0 0 24 24">
                          <circle cx="9" cy="6" r="1.4" /><circle cx="15" cy="6" r="1.4" />
                          <circle cx="9" cy="12" r="1.4" /><circle cx="15" cy="12" r="1.4" />
                          <circle cx="9" cy="18" r="1.4" /><circle cx="15" cy="18" r="1.4" />
                        </svg>
                      </span>
                      <span className="mm-item-title">{it.label || "(no label)"}</span>
                      <span className="mm-item-depth">L{it.depth + 1}</span>
                      <button type="button" className="mm-edit-btn" onClick={function(){ toggleEdit(it.id); }} title="Edit">
                        <svg className="mm-svg" viewBox="0 0 24 24"><path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      </button>
                    </div>
                    <div className="mm-item-edit" style={{ marginLeft: (it.depth * INDENT_PX) + "px" }}>
                      <div className="mm-edit-inner">
                        {/* Read on blur only — no per-keystroke handler. The box
                            is uncontrolled, so it shows what you type on its
                            own; nothing else needs to know until the edit is
                            finished. Blur runs before the click that caused it,
                            so Save, the close button, the Up/Down/Out/In and
                            Remove buttons and the drag handle all see the
                            committed label. See updateField above. */}
                        <input key={"lbl" + labelRev} className="mm-txt" type="text" defaultValue={it.label}
                               maxLength={LABEL_MAX}
                               onBlur={function(e){ updateField(i, "label", e.currentTarget.value); commitField(); }} />
                        {/* No raw URL box. For an email, phone, web address or
                            anchor, Edit reopens the picker on that item's tab
                            with its values filled in, so the value is always
                            built by the same code that first wrote it. Internal
                            pages have no fields to re-fill — replace one by
                            picking again. */}
                        <div className="mm-row-actions">
                          {tabForValue(it.url)
                            ? <button type="button" className="mm-btn mm-btn-sm mm-btn-primary"
                                      onClick={function(){ openEdit(i); }}>{t("edit", "Edit")}</button>
                            : null}
                          <button type="button" className="mm-btn mm-btn-sm" onClick={function(){ moveBy(i, -1); }} disabled={i === 0}>↑ {t("up", "Up")}</button>
                          <button type="button" className="mm-btn mm-btn-sm" onClick={function(){ moveBy(i, 1); }} disabled={i >= items.length-1}>↓ {t("down", "Down")}</button>
                          <button type="button" className="mm-btn mm-btn-sm" onClick={function(){ reIndent(i, -1); }} disabled={it.depth === 0}>← {t("menu_outdent", "Out")}</button>
                          <button type="button" className="mm-btn mm-btn-sm" onClick={function(){ reIndent(i, 1); }} disabled={it.depth >= maxAllowed}>→ {t("menu_indent", "In")}</button>
                          <button type="button" className="mm-btn mm-btn-sm mm-btn-danger" onClick={function(){ removeItem(i); }} style={{ marginLeft: "auto" }}>{t("remove", "Remove")}</button>
                        </div>
                      </div>
                    </div>
                  </li>;
                })}

                {drag ? <li className="mm-placeholder" style={{ marginLeft: (drag.depth * INDENT_PX) + "px" }} /> : null}
              </ul>
            </div>

            {/* Add menu items — a button at the foot of the card rather than a
                permanent column, so the structure gets the full width until you
                actually want to add something. */}
            <div className="mm-card-foot">
              <button type="button" className="mm-btn mm-btn-primary mm-btn-full"
                      onClick={function(){ if (addOpen) closeAdd(); else openAdd(); }} disabled={!customOn}
                      aria-expanded={addOpen}>
                {addOpen ? "Close" : "Add menu items"}
              </button>
            </div>

          </section>

        </div>
      </div>

      {drag ? <div className="mm-drag-ghost" style={{ top: (drag.curY + 12) + "px", left: (drag.curX + 12) + "px" }}>
        {drag.label + (drag.ids.length > 1 ? "  (+" + (drag.ids.length - 1) + ")" : "")}
      </div> : null}

    </div>

    {/* Add menu items — its own popup rather than a column or a drawer, so the
        structure keeps the full card and the picker gets room of its own. */}
    {addOpen ? <div className="mm-add-overlay" onClick={closeAdd}>
      <div className="mm-add-popup" role="dialog" aria-modal="true"
           onClick={function(e){ e.stopPropagation(); }}>
        <header className="mm-add-head">
          <h2 className="mm-add-title">{editIndex === null ? "Add menu items" : "Edit menu item"}</h2>
          <button type="button" className="mm-close" onClick={closeAdd} aria-label={t("close", "Close")}>×</button>
        </header>

        {/* Sidebar tabs on the left, panel on the right — the LINK EDITOR's
            layout. A horizontal strip cost the panel a row of height and read
            as a different editor; this way the tab list can grow without
            eating into the page tree or the anchor preview. */}
        <div className="mm-add-body">
              <nav className="mm-add-nav" role="tablist">
                {ADD_TABS.map(function(t){
                  return <button key={t.id} type="button" role="tab"
                          aria-selected={openPanel === t.id}
                          className={"mm-tab" + (openPanel === t.id ? " mm-tab-on" : "")}
                          onClick={function(){ setOpenPanel(t.id); }}>{app.getLang(t.lang)}</button>;
                })}
              </nav>

              {/* Every tab ends in the same Save button, pinned to the bottom of
                  the panel by .mm-panel-foot — including Anchors, where picking a
                  section now only stages it. Nothing is added to the menu until
                  Save, so a tab is never committing behind your back. */}
              <div className={"mm-tabpanel" + (openPanel === "anchor" ? " mm-tabpanel-flush" : "")} role="tabpanel">
                {openPanel === "pages" ? <>
                  {renderPageTree(availablePages, checkedPages, setCheckedPages)}
                  <div className="mm-panel-foot">
                    <button type="button" className="mm-btn mm-btn-primary"
                            onClick={doAddPages}
                            disabled={!Object.keys(checkedPages).some(function(k){ return checkedPages[k]; })}>
                      {app.getLang("save")}
                    </button>
                  </div>
                </> : null}

                {openPanel === "anchor" ? <>
                  {/* AnchorBlocks brings its own page tree and section preview,
                      and reports "<pageUrl>#<blockId>"; onLegacyAnchorValue
                      rewrites the path half to the page's pageId and stages it. */}
                  <div className="mm-anchor-legacy">
                    <AnchorBlocks onPick={onLegacyAnchorValue} value={anchorPick ? anchorPick.url : ""} />
                  </div>
                  <div className="mm-panel-foot">
                    {/* Say what Save will add — the picker gives no feedback of
                        its own once the click is over. Nothing shown until
                        something is picked. */}
                    {/* Siblings, not nested: .mm-picked is nowrap + ellipsis so
                        a long pageId stays on one line, and a hint inside it
                        would be ellipsised away with the id. */}
                    {anchorPick
                      ? <>
                          <p className="mm-picked">
                            Selected: <strong>{anchorPick.label || anchorPick.url}</strong> <code>{anchorPick.url}</code>
                          </p>
                          <p className="mm-picked-hint">{t("click_save_hint", "Click Save to add it to the menu.")}</p>
                        </>
                      : <p className="mm-picked-hint">{t("pick_section_hint", "Pick a section from a page above to link to.")}</p>}
                    <button type="button" className="mm-btn mm-btn-primary"
                            onClick={doAddAnchor} disabled={!anchorPick}>
                      {app.getLang("save")}
                    </button>
                  </div>
                </> : null}

                {openPanel === "custom" ? <>
                  <label className="mm-fld">{t("link_text", "Link text")}</label>
                  {/* autoFocus so the field is live the moment the panel mounts.
                      Without it focus stays on the tab that was clicked, and
                      anything typed before the author clicks the box is dropped
                      on the floor — the other way a label loses its opening
                      characters. Fires on mount, which is exactly when the
                      panel opens, since it is mounted conditionally above. */}
                  <input className="mm-txt" type="text" autoFocus placeholder={t("menu_item", "Menu item")} maxLength={LABEL_MAX} value={customText} onChange={function(e){ setCustomText(e.target.value); }} />
                  <label className="mm-fld">{app.getLang("url")}</label>
                  <input className="mm-txt" type="text" placeholder="https://" value={customUrl} onChange={function(e){ setCustomUrl(e.target.value); }} />
                  <div className="mm-panel-foot">
                    <button type="button" className="mm-btn mm-btn-primary"
                            onClick={doAddCustom} disabled={!customUrl.trim() || customUrl.trim() === "https://" || isUnsafeHref(customUrl)}>
                      {app.getLang("save")}
                    </button>
                  </div>
                </> : null}

                {openPanel === "email" ? <>
                  <label className="mm-fld">{t("link_text", "Link text")}</label>
                  <input className="mm-txt" type="text" placeholder={t("email_us", "Email us")} maxLength={LABEL_MAX} value={emailText} onChange={function(e){ setEmailText(e.target.value); }} />
                  <label className="mm-fld">{app.getLang("to")}</label>
                  <input className="mm-txt" type="text" placeholder={t("add_email", "Add email")} value={emailAddr} onChange={function(e){ setEmailAddr(e.target.value); }} />
                  <label className="mm-fld">{app.getLang("subject")}</label>
                  <input className="mm-txt" type="text" placeholder={t("email_subject", "Add email subject")} value={emailSubject} onChange={function(e){ setEmailSubject(e.target.value); }} />
                  <label className="mm-fld">{app.getLang("body")}</label>
                  <textarea className="mm-txt mm-area" rows="4" placeholder={t("email_content", "Add email content")} value={emailBody} onChange={function(e){ setEmailBody(e.target.value); }} />
                  <div className="mm-panel-foot">
                    <button type="button" className="mm-btn mm-btn-primary"
                            onClick={doAddEmail} disabled={!emailAddr.trim()}>
                      {app.getLang("save")}
                    </button>
                  </div>
                </> : null}

                {openPanel === "phone" ? <>
                  <label className="mm-fld">{t("link_text", "Link text")}</label>
                  <input className="mm-txt" type="text" placeholder={t("call_us", "Call us")} maxLength={LABEL_MAX} value={phoneText} onChange={function(e){ setPhoneText(e.target.value); }} />
                  <label className="mm-fld">{app.getLang("phoneNumber")}</label>
                  {/* A native select rather than the LINK EDITOR's bootstrap
                      dropdown: same ~240 entries and the browser's own type-ahead
                      does the searching, with no ui.css dependency to drag in.
                      Keyed by row index, not dialCode: 9 dial codes are shared by
                      more than one country (+61 by three), and duplicate option
                      values make the select snap to the first match instead of
                      the country that was clicked. */}
                  <div className="mm-phone-row">
                    <select className="mm-txt mm-dial" value={phoneIdx}
                            onChange={function(e){ setPhoneIdx(e.target.value); }}
                            aria-label={t("country_code", "Country code")}>
                      {countryCode.map(function(c, i){
                        return <option key={i} value={String(i)}>
                          {c.dialCode === "none" ? "none" : c.dialCode + " " + c.name}
                        </option>;
                      })}
                    </select>
                    <input className="mm-txt" type="text" placeholder="xx-xxxx-xxxx" value={phoneNum} onChange={function(e){ setPhoneNum(e.target.value); }} />
                  </div>
                  <div className="mm-panel-foot">
                    <button type="button" className="mm-btn mm-btn-primary"
                            onClick={doAddPhone} disabled={!phoneNum.replace(/[^0-9]/g, "")}>
                      {app.getLang("save")}
                    </button>
                  </div>
                </> : null}
              </div>
        </div>
      </div>
    </div> : null}
  </>, mountNode);
}

// ─── Tree renderer for the Pages panel ──────────────────
// Vertical connector line on the left, icon per page, checkbox per node.
// Checking a folder cascades to all descendants; unchecking cascades the
// same way. This way selecting a parent behaves like "grab this whole
// subtree" which is what users expect from a tree with checkboxes.
function renderPageTree(nodes, checked, setChecked){
  if (!nodes || !nodes.length) return <div className="mm-empty">{t("no_pages_available", "No pages available.")}</div>;
  // Collect id + every descendant id for the cascade.
  const collectSubtreeIds = function(p){
    var ids = [p.id];
    if (p.children && p.children.length) {
      p.children.forEach(function(c){ ids = ids.concat(collectSubtreeIds(c)); });
    }
    return ids;
  };
  const walk = function(list, depth){
    return <ul className={"mm-pg-tree" + (depth === 0 ? " mm-pg-root" : "")}>
      {list.map(function(p){
        var isChecked = !!checked[p.id];
        var kids = p.children && p.children.length ? p.children : null;
        return <li key={p.id} className="mm-pg-node">
          <label className="mm-pg-row">
            <input type="checkbox" checked={isChecked} onChange={function(e){
              var v = e.target.checked;
              var ids = collectSubtreeIds(p);
              setChecked(function(prev){
                var next = { ...prev };
                ids.forEach(function(id){ next[id] = v; });
                return next;
              });
            }} />
            {p.pageIcon
              ? <i className={"mm-pg-icon-font " + p.pageIcon} aria-hidden="true" />
              : <PageIcon name={p.icon} />}
            <span className="mm-pg-label">{p.label}</span>
          </label>
          {kids ? walk(kids, depth + 1) : null}
        </li>;
      })}
    </ul>;
  };
  return <div className="mm-pg-tree-wrap">{walk(nodes, 0)}</div>;
}

// ─── Anchors picker ──────────────────────────────────────────────────
//
// A local copy of LinkBlocks (src/components/helpers.js), which the LINK EDITOR
// still uses. Copied rather than imported so this file has no dependency on
// helpers.js — helpers.js imports ManageMenu, and importing back created a
// cycle that only worked because the binding was read at render time.
//
// Behaviour is the same: list the app's pages, click one to load it in the
// preview app, and the preview posts back the id of the section you click
// there. onPick receives "<pageUrl>#<blockId>".
//
// Markup keeps ui.css's class names (.listParent, .tree, .pwa-iframe-*) so the
// existing styling and the overrides in MM_STYLES apply unchanged.

// Page types that can never yield an anchor. An anchor targets a block's viewId
// inside pageDataJson; these store {webJson, mobileJson, webHtml} — or nothing
// at all — instead of an array of blocks, so there is nothing for the browser
// to scroll to. LinkBlocks lists them anyway (it only hides isAIPage === 0),
// which on a real app meant 4 of the 14 visible rows opened a preview with
// nothing selectable in it.
const NO_BLOCK_TYPES = { aifeature: true, htmlcode: true };

// Rows worth listing. Folders survive only while something inside them does —
// an empty folder is a row you cannot click wrapped around nothing.
const canHoldAnchors = function(v){
  if (!v) return false;
  if (v.pageIdentifier === "folder") return (v.folderPages || []).some(canHoldAnchors);
  if (v.isAIPage === 0) return false;                    // same rule as LinkBlocks
  return !NO_BLOCK_TYPES[v.pageIdentifier];
};

// `value` is the item's stored anchor when editing, "<pageId>#<viewId>". The
// half before the "#" is a pageId, so the matching row is marked active on open
// — otherwise nothing is highlighted until you click, and an edit gives no clue
// which page the anchor already points at.
const AnchorBlocks = function({ onPick, value }){
  const activePageId = String(value || "").split("#")[0];
  const [pages, setPages] = useState([]);
  const [isPreview, setIsPreview] = useState(false);
  const [pageUrl, setPageUrl] = useState(null);
  const [pageId, setPageId] = useState(null);
  // The row whose preview is open. Reported back with the picked section so the
  // caller can label the menu item with the page's own title rather than
  // re-deriving it from the path.
  const [pageName, setPageName] = useState("");

  useEffect(function(){
    app.fetchPost({
      method: "getAllFeaturePages",
      appId: localStorage.getItem("aiAppId")
    }).then(function(response){
      if (response && response.data) setPages(response.data);
    }).catch(function(err){
      console.error("getAllFeaturePages failed for the anchor picker", err);
    });
  }, []);

  // Held in a ref so the listener below depends on pageUrl alone. The caller
  // passes an inline function, so depending on onPick directly would tear the
  // listener down and rebuild it on every render.
  const onPickRef = useRef(onPick);
  onPickRef.current = onPick;

  // The preview app posts { data: "<widget-id>" } when a section is clicked.
  // Re-bound on the open page so the id is paired with what is actually showing.
  useEffect(function(){
    const onMessage = function(event){
      const blockId = event.data && event.data.data;
      if (blockId && onPickRef.current) {
        onPickRef.current(`${pageUrl}#${blockId}`, { pageId: pageId, pageName: pageName });
      }
      setIsPreview(false);
    };
    window.addEventListener("message", onMessage);
    return function(){ window.removeEventListener("message", onMessage); };
  }, [pageUrl, pageId, pageName]);

  const openPreview = function(page, url){
    setPageId(page.pageId);
    setPageName(page.pageName || "");
    setPageUrl(url);
    setIsPreview(true);
  };

  // A folder with pages of its own is a container, not a target — it has no
  // sections to anchor to, so it is shown but not clickable.
  const renderRow = function(v, url){
    const isContainer = !!(v.folderPages && v.folderPages.length);
    // "on" for the row you just clicked, or the one the edited value points at.
    const isActive = pageUrl === url || (!!activePageId && v.pageId === activePageId);
    return <li key={v.pageId || url}>
      <a className={`${v.pageIcon || ""} ${isActive ? "on" : ""}`}
         style={isContainer ? { pointerEvents: "none", cursor: "not-allowed", opacity: "0.8" } : undefined}
         data-url={url}
         onClick={function(){ openPreview(v, url); }}>{v.pageName}</a>
      <button type="button">{app.getLang("select")}</button>
      {renderFolder(v, url)}
    </li>;
  };
  const renderFolder = function(parent, parentUrl){
    const kids = (parent.folderPages || []).filter(canHoldAnchors);
    if (!kids.length) return null;
    return <ul>
      {kids.map(function(v){ return renderRow(v, `${parentUrl}/${v.pageUrl}`); })}
    </ul>;
  };

  let previewHost = "https://preview.mediadirhub.com";
  if (window.location.host.indexOf("pbodev.info") !== -1 || window.location.hostname.indexOf("localhost") !== -1) {
    previewHost = "https://ai-builder-preview.pages.dev";
  }

  // "home" is what LinkBlocks tests for, but the API sends "homenew" — so home
  // fell through to `/PAGE_IDENTIFIER_HOME`, which matches no page and leaves
  // getLinkURL with a dead href. Both spellings map to "/", matching what
  // normalizePage above already does for the Internal Page tab.
  const rows = pages.filter(canHoldAnchors);

  return <div className="listParent">
    {!isPreview
      ? (rows.length
          ? <ul className="tree">
              {rows.map(function(v){
                const isHome = v.pageIdentifier === "home" || v.pageIdentifier === "homenew";
                return renderRow(v, isHome ? "/" : `/${v.pageUrl}`);
              })}
            </ul>
          : <div className="mm-empty">{t("no_anchor_pages", "No pages with anchorable sections.")}</div>)
      : <div className="pwa-iframe-container">
          <span className="close-pwa-iframe" onClick={function(){ setIsPreview(false); }}>
            <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#666666"><path d="m256-200-56-56 224-224-224-224 56-56 224 224 224-224 56 56-224 224 224 224-56 56-224-224-224 224Z"></path></svg>
          </span>
          <div className="pwa-iframe-wrapper">
            <iframe
              title="Page preview"
              className="pwa-iframe"
              src={`${previewHost}/${app.getDevice() ? "desktop" : "mobile"}-anchor/${localStorage.getItem("aiAppId")}/${pageId}?lang=${app.appLang()}`}
            ></iframe>
          </div>
        </div>}
  </div>;
};

// ─── Styles (light theme, mm- prefix scoped) ─────────────────────────

const MM_STYLES = `
.mm-backdrop {
  /* absolute (not fixed) so it fits the app container we're portaled into */
  position: absolute; inset: 0;
  background: rgba(15, 23, 42, 0.35);
  z-index: 9998;
  /* Force pointer-events on because the popup may be rendered inside
     an .app-overlay parent that sets pointer-events:none. */
  pointer-events: auto;
}
.mm-popup {
  /* absolute — fills the app container, NOT the browser viewport, so the
     popup stays inside the container even when the app is embedded */
  position: absolute;
  top: 80px; left: 10px; right: 10px; bottom: 10px;
  /* Capped and centred rather than stretching to the container. left/right stay
     as the 10px gutter so it still shrinks on narrow containers; margin:auto
     centres it once the cap kicks in. */
  max-width: 900px;
  margin: 0 auto;
  background: #f5f6f8;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(15, 23, 42, 0.25);
  z-index: 9999;
  display: flex; flex-direction: column;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  color: #111827;
  overflow: hidden;
  /* Override any pointer-events:none inherited from an .app-overlay parent. */
  pointer-events: auto;
}

/* Pickers opened FROM this popup — CHOOSE BACKGROUND, the link editor, the
   connection model — mount into document.body and carry bootstrap's .modal
   z-index of 1060, far below the mm-* layers (backdrop 9998, popup 9999, drag
   ghost 10000, add-items overlay 10001), so they opened *behind* the menu.
   10050 clears all four with room to spare.

   Matched via .ai-popup-layer, the class app.imagePopup / linkPopup /
   connectionModel put on the container div they create. Targeting a bare
   ".modal" instead is far too blunt: the builder host page carries ~74 .modal
   elements of its own (measured on 72snappy), and a plain rule silently
   rewrote 37 of them.

   Still scoped to MM_STYLES so it only exists while this popup is mounted, and
   the two-class selector also outranks bootstrap on specificity rather than
   relying on injection order.

   No backticks anywhere in here - MM_STYLES is a template literal. */
body .ai-popup-layer .modal { z-index: 10050; }

/* Same collision, different owner: the builder host page's publish-success
   modal. Clicking Save with this popup open painted "Your site is published"
   behind the menu, because that modal is the host's own #publishPopUp - an
   AngularJS node, position fixed, z-index 1060 - and 1060 is under all four
   mm-* layers above.

   Matched by id rather than by class. Its classes (modal creator-modal
   modal-custom modal-header-none modal-live-app) are shared with other host
   modals, and per the note above a blunt .modal rule here rewrote 37 of the
   host page's ~74 modals. The id is unique and is the host's, not ours - this
   component does not render it. src/components/helpers.js exports a
   Congratulations class with near-identical copy, but nothing imports it, and
   the live text differs ("live onlive" vs "live online"), so the host template
   is what actually paints.

   10060 clears .ai-popup-layer .modal at 10050 as well, so a picker opened
   from the menu still cannot cover the publish confirmation.

   In MM_STYLES on purpose: the override then exists only while the menu popup
   is mounted, leaving the host page's own stacking untouched the rest of the
   time - it has a modal of its own sitting at 99999999. */
body #publishPopUp { z-index: 10060; }

.mm-header {
  display: flex; align-items: center; gap: 20px;
  padding: 14px 20px;
  border-bottom: 1px solid #e5e7eb;
  background: #fff;
  flex: 0 0 auto;
}
.mm-title { flex: 1 1 auto; margin: 0; font-size: 16px; font-weight: 600; color: #111827; }
.mm-toggle {
  display: inline-flex; align-items: center; gap: 10px;
  cursor: pointer; font-size: 13px; font-weight: 500; color: #374151;
  padding: 0 4px;
}
.mm-toggle input { display: none; }
.mm-toggle-switch {
  width: 34px; height: 20px; border-radius: 20px; background: #d1d5db;
  position: relative; transition: background 0.15s ease;
}
.mm-toggle-switch::after {
  content: ""; position: absolute; top: 2px; left: 2px;
  width: 16px; height: 16px; border-radius: 50%; background: #fff;
  box-shadow: 0 1px 2px rgba(0,0,0,0.15);
  transition: transform 0.15s ease;
}
.mm-toggle-switch.mm-on { background: #3899ED; }
.mm-toggle-switch.mm-on::after { transform: translateX(14px); }
.mm-close {
  border: 0; background: transparent; font-size: 22px; line-height: 1;
  cursor: pointer; padding: 4px 10px; color: #6b7280; border-radius: 6px;
}
.mm-close:hover { background: #f3f4f6; color: #111827; }
.mm-card-disabled { opacity: 0.5; pointer-events: none; user-select: none; }

.mm-body {
  flex: 1 1 auto;
  display: grid;
  grid-template-columns: 320px 1fr 1.2fr;
  gap: 10px;
  padding: 10px;
  min-height: 0;
  overflow: hidden;                         /* no outer scroll — each card scrolls independently */
}
@media (max-width: 900px) { .mm-body { grid-template-columns: 1fr; overflow: auto; } }
.mm-col { display: flex; flex-direction: column; gap: 10px; min-width: 0; min-height: 0; }

.mm-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  display: flex;                            /* stack head + body vertically */
  flex-direction: column;
  flex: 1 1 auto;                           /* fill the column height */
  min-height: 0;                            /* allow inner overflow to scroll */
}
.mm-card-head {
  padding: 14px 16px;
  border-bottom: 1px solid #e5e7eb;
  font-size: 14px; font-weight: 600; color: #111827;
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
  background: #fafbfc;
}
.mm-card-body {
  padding: 14px 16px;
  flex: 1 1 auto;                           /* fills remaining space */
  overflow-y: auto;                         /* internal scroll — no page-level scroll */
  min-height: 0;                            /* required for flex-child overflow */
}
.mm-card-list { padding: 6px; }

/* Accordion */
/* Tab sidebar — mirrors the LINK EDITOR's .sidebar-nav (ui.css): grey column,
   14px links, #3C4055 at 500 going #3899ED at 700 when selected. Kept as real
   values rather than reusing .sidebar-nav so this popup stays self-contained
   and does not shift if ui.css changes underneath it. */
.mm-add-nav {
  flex: 0 0 auto; width: 208px;
  display: flex; flex-direction: column; align-items: stretch;
  background: #EDEFF3; padding: 8px 16px 16px;
  overflow-y: auto;
}
.mm-tab {
  background: transparent; border: 0;
  color: #3C4055; font-size: 14px; font-weight: 500;
  padding: 12px 0; cursor: pointer; text-align: left;
  transition: color 0.12s ease;
}
.mm-tab:hover { color: #111827; }
.mm-tab-on { color: #3899ED; font-weight: 700; }
.mm-tabpanel {
  flex: 1 1 auto;                          /* fills remaining space in the card */
  overflow-y: auto;                        /* independent scroll */
  min-height: 0;                           /* required for flex-child overflow */
  padding: 12px 14px 14px;
}

.mm-fld { display: block; font-size: 12px; color: #6b7280; margin: 8px 0 4px; }
.mm-txt {
  width: 100%; background: #ffffff; border: 1px solid #d1d5db; border-radius: 8px;
  color: #111827; font-size: 13px; padding: 9px 11px; outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  box-sizing: border-box;
}
.mm-txt:focus { border-color: #3899ED; box-shadow: 0 0 0 3px rgba(56,153,237,0.15); }
/* Email body: multi-line, resizable vertically only so it cannot break the
   panel's width. flex:0 0 auto keeps the column layout from stretching it. */
.mm-area { flex: 0 0 auto; resize: vertical; min-height: 84px; font-family: inherit; line-height: 1.45; }

/* Buttons */
.mm-btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  cursor: pointer; border: 1px solid #d1d5db; background: #ffffff; color: #111827;
  font-size: 13px; font-weight: 500; padding: 9px 14px; border-radius: 8px;
  transition: border-color 0.15s ease, background 0.12s ease;
}
.mm-btn:hover:not(:disabled) { border-color: #3899ED; background: #f9fafb; }
.mm-btn:disabled { opacity: 0.4; cursor: default; }
.mm-btn-primary { background: #3899ED; border-color: #3899ED; color: #fff; }
/* Must restate background and border-color, not just tweak a filter: the
   generic .mm-btn:hover:not(:disabled) above is (0,3,0) and outranks
   .mm-btn-primary at (0,1,0), so on hover it painted #f9fafb under the white
   label and the button read as blank. Equal specificity here, declared later,
   so this wins. */
.mm-btn-primary:hover:not(:disabled) { background: #2b86d4; border-color: #2b86d4; color: #fff; }
.mm-btn-full { width: 100%; margin-top: 12px; }
.mm-btn-sm { padding: 6px 10px; font-size: 12px; border-radius: 6px; }
.mm-btn-ghost { background: transparent; border-color: transparent; color: #6b7280; }
.mm-btn-ghost:hover:not(:disabled) { color: #111827; background: #f3f4f6; }
.mm-btn-danger { color: #ef4444; border-color: transparent; background: transparent; }
.mm-btn-danger:hover:not(:disabled) { background: #fef2f2; color: #b91c1c; border-color: #fecaca; }
.mm-row-actions { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 12px; }

/* Preset pages */
/* Sortable menu */
.mm-menu-list { list-style: none; margin: 0; padding: 8px; min-height: 60px; position: relative; }
.mm-item { margin: 0; }
.mm-item-row {
  display: flex; align-items: center; gap: 8px;
  background: #ffffff; border: 1px solid #e5e7eb; border-radius: 8px;
  padding: 0 10px 0 0; margin: 4px 0;
  transition: margin-left 0.2s cubic-bezier(.4,0,.2,1), box-shadow 0.15s ease, border-color 0.15s ease;
}
.mm-item-row:hover { border-color: #3899ED; box-shadow: 0 2px 8px rgba(15,23,42,0.06); }
.mm-handle {
  flex: 0 0 auto; width: 34px; align-self: stretch;
  display: flex; align-items: center; justify-content: center;
  color: #9ca3af; cursor: grab; touch-action: none;
  border-right: 1px solid #e5e7eb;
}
.mm-handle:hover { color: #6b7280; background: #f9fafb; }
.mm-handle:active { cursor: grabbing; }
.mm-item-title {
  flex: 1 1 auto; min-width: 0; padding: 11px 0;
  font-size: 13px; font-weight: 500; color: #111827;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.mm-item-depth { flex: 0 0 auto; font-size: 11px; color: #6b7280; background: #f3f4f6; padding: 2px 8px; border-radius: 20px; font-weight: 600; }
.mm-edit-btn {
  flex: 0 0 auto; border: 0; background: transparent; color: #9ca3af; cursor: pointer;
  width: 30px; height: 30px; border-radius: 7px;
  display: flex; align-items: center; justify-content: center;
  transition: transform 0.2s ease, background 0.12s ease, color 0.12s ease;
}
.mm-edit-btn:hover { background: #f3f4f6; color: #111827; }
.mm-editing .mm-edit-btn { transform: rotate(180deg); color: #3899ED; }

.mm-item-edit { display: grid; grid-template-rows: 0fr; transition: grid-template-rows 0.2s cubic-bezier(.4,0,.2,1); }
.mm-editing .mm-item-edit { grid-template-rows: 1fr; }
.mm-item-edit > .mm-edit-inner {
  min-height: 0; overflow: hidden;
  background: #fafbfc; border: 1px solid #e5e7eb; border-top: 0;
  border-radius: 0 0 8px 8px; margin: -6px 0 6px; padding: 0 12px;
}
.mm-editing .mm-edit-inner { padding: 12px; }

.mm-item.mm-dragging { opacity: 0.35; pointer-events: none; }
.mm-item.mm-dragging .mm-item-row { box-shadow: none; border-style: dashed; }
.mm-placeholder {
  height: 42px; border: 2px dashed #3899ED; border-radius: 8px;
  background: rgba(56,153,237,0.14); margin: 4px 0;
}
.mm-drag-ghost {
  position: fixed; z-index: 10000; pointer-events: none; opacity: 0.95;
  background: #111827; color: #fff; border: 1px solid #3899ED; border-radius: 8px;
  padding: 8px 12px; font-size: 13px; font-weight: 500;
  box-shadow: 0 10px 30px rgba(0,0,0,0.25);
  max-width: 280px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}

.mm-empty { color: #9ca3af; font-size: 13px; padding: 14px; text-align: center; }

.mm-svg { width: 16px; height: 16px; stroke: currentColor; fill: currentColor; }

/* Host for AnchorBlocks inside the Anchors panel. Its markup uses ui.css
   classes (.listParent, .tree, .pwa-iframe-*), so only the box is sized here. */
/* No max-height/overflow of its own — .mm-tabpanel already scrolls, and a
   second scroller here would clip the section preview. */
.mm-anchor-legacy { margin-top: 2px; }

/* ── ManageMenu two-card layout ──────────────────────────────────────── */
/* Two cards, not three columns: design on the left, structure on the right. */
.mm-body-2col { grid-template-columns: minmax(280px, 380px) 1fr; }
@media (max-width: 900px) { .mm-body-2col { grid-template-columns: 1fr; } }

/* Toggle sized to sit inside a card head next to the title */

/* Foot of the structure card — holds the Add menu items button */
.mm-card-foot {
  flex: 0 0 auto;
  padding: 10px 12px;
  border-top: 1px solid #e5e7eb;
  background: #fbfbfc;
}

/* Add menu items popup. Fixed to the viewport, not the editor container, so the
   picker is not constrained by the card it was opened from. */
.mm-add-overlay {
  position: fixed; inset: 0; z-index: 10001;
  background: rgba(17, 24, 39, 0.45);
  display: flex; align-items: center; justify-content: center; padding: 24px;
}
.mm-add-popup {
  background: #ffffff; border-radius: 12px; overflow: hidden;
  /* Wider than the old strip layout: the sidebar takes a fixed 208px, so the
     panel keeps roughly the width it had before the tabs moved to the side. */
  width: min(880px, 100%); height: min(78vh, 100%);
  display: flex; flex-direction: column;
  box-shadow: 0 24px 64px rgba(16, 24, 40, 0.32);
}
.mm-add-head {
  flex: 0 0 auto; display: flex; align-items: center; justify-content: space-between;
  gap: 12px; padding: 12px 16px; border-bottom: 1px solid #e5e7eb;
}
.mm-add-title { margin: 0; font-size: 14px; font-weight: 700; color: #111827; }
/* Sidebar + panel share the row below the header; each scrolls on its own. */
.mm-add-body { flex: 1 1 auto; display: flex; min-height: 0; }
.mm-add-popup .mm-tabpanel { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; }
/* The panel is a flex column so the foot can be pushed to the bottom; its own
   labels and fields must keep their natural height rather than being squeezed. */
.mm-add-popup .mm-tabpanel > .mm-fld,
.mm-add-popup .mm-tabpanel > .mm-txt { flex: 0 0 auto; }
/* The 320px cap suited the old narrow column. Here the panel is a column, so
   the tree takes whatever height is left and the button stays pinned below it
   instead of floating in a band of empty white. */
.mm-add-popup .mm-pg-tree-wrap { max-height: none; flex: 1 1 auto; min-height: 0; }
/* Save sits at the bottom of every panel. margin-top:auto pushes it down when
   the fields are short, so the button is in one place whichever tab is open.
   Right-aligned at its natural width, like the LINK EDITOR's own Save. */
.mm-panel-foot {
  flex: 0 0 auto; margin-top: auto; padding-top: 14px;
  border-top: 1px solid #e5e7eb;
  display: flex; flex-direction: column; align-items: flex-end;
}
.mm-panel-foot .mm-btn { min-width: 120px; }
/* Anchors takes the height the panel actually has, then scrolls. ui.css caps
   .listParent at max-height:300px, which left a third of the panel empty while
   the page tree scrolled inside a short box — so the cap is lifted here and the
   scroll moved to the height this panel gives it. */
.mm-add-popup .mm-anchor-legacy {
  flex: 1 1 auto; min-height: 0;
  display: flex; flex-direction: column;
}
.mm-add-popup .mm-anchor-legacy .listParent {
  max-height: none; flex: 1 1 auto; min-height: 0; overflow-y: auto;
  display: flex; flex-direction: column;
}
/* Page preview. ui.css gives the wrapper a fixed 350px and the container a
   25px bottom margin, which left the frame floating in the middle of the panel
   with ~80px dead below it. Here the whole thing is a column that fills the
   anchor area: the frame takes the leftover height and the full panel width. */
.mm-add-popup .mm-anchor-legacy .pwa-iframe-container {
  flex: 1 1 auto; min-height: 0;
  display: flex; flex-direction: column;
  margin-bottom: 0; padding-top: 30px;
}
.mm-add-popup .mm-anchor-legacy .pwa-iframe-wrapper {
  flex: 1 1 auto; min-height: 0; height: auto;
  width: 100%; margin: 0;
  display: flex;                           /* so the iframe can fill it */
}
.mm-add-popup .mm-anchor-legacy iframe.pwa-iframe { flex: 1 1 auto; height: auto; min-height: 0; }
/* Above the frame, hard against the panel's top-right corner. z-index because
   the frame is a sibling that would otherwise sit over it. */
.mm-add-popup .mm-anchor-legacy .close-pwa-iframe { top: 0; right: 0; z-index: 2; }
/* The preview spans the panel edge to edge, so the panel drops its own side
   padding on this tab; the Save row keeps its inset. */
.mm-add-popup .mm-tabpanel-flush { padding: 0 0 14px; }
.mm-add-popup .mm-tabpanel-flush > .mm-panel-foot { margin: 0 14px; }
.mm-add-popup .mm-tabpanel-flush .mm-anchor-legacy { margin-top: 0; }
.mm-add-popup .mm-tabpanel-flush .tree { padding: 10px 14px 0; }
.mm-picked {
  align-self: stretch;                     /* the foot right-aligns its children */
  margin: 0 0 10px; font-size: 12px; color: #6b7280;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.mm-picked strong { color: #111827; }
.mm-picked code { color: #3899ED; font-size: 11px; }
/* Sits under the Selected line, or stands alone before anything is picked, so
   the panel always says what to do next. Wraps - unlike .mm-picked, which is
   held to one line so a long pageId ellipsises rather than reflowing. */
.mm-picked-hint {
  align-self: stretch;
  margin: 0 0 10px; font-size: 12px; color: #9ca3af;
  white-space: normal;
}
/* Dial code + number on one line, as the LINK EDITOR has it. The select is
   capped so a long country name cannot squeeze out the number field. */
.mm-phone-row { display: flex; gap: 8px; align-items: center; }
.mm-phone-row .mm-dial { flex: 0 0 auto; width: 130px; cursor: pointer; }
.mm-phone-row input { flex: 1 1 auto; min-width: 0; }
/* Narrow containers cannot spare 208px for a sidebar — fall back to the
   horizontal strip the LINK EDITOR itself uses below its sm breakpoint. */
@media (max-width: 640px) {
  .mm-add-body { flex-direction: column; }
  .mm-add-nav {
    width: auto; flex-direction: row; gap: 20px;
    padding: 0 12px; overflow-x: auto; white-space: nowrap;
  }
  .mm-tab { padding: 14px 2px; font-size: 13px; text-transform: uppercase; }
  .mm-tab-on { box-shadow: inset 0 -2px 0 #3899ED; }
}
/* Host for the builder's own style panel. Its markup uses ui.css classes, so
   only the box is controlled here; the card body already scrolls. */
.mm-design-legacy { margin: -4px -2px 0; }
.mm-design-legacy .builder-widget { margin-bottom: 10px; }
.mm-anchor-legacy .pwa-iframe { width: 100%; border: 0; }

.mm-pg-tree-wrap { margin-top: 4px; max-height: 320px; overflow-y: auto; padding: 4px 0; }
.mm-pg-tree { list-style: none; margin: 0; padding: 0; }
.mm-pg-root { border-left: 1px solid #e5e7eb; padding-left: 4px; }
.mm-pg-tree .mm-pg-tree { padding-left: 20px; border-left: 1px solid #e5e7eb; margin-left: 14px; }
.mm-pg-node { position: relative; margin: 2px 0; }
.mm-pg-row {
  display: flex; align-items: center; gap: 10px;
  padding: 6px 10px; border-radius: 6px;
  color: #374151; font-size: 13px; cursor: pointer; margin: 0;
  transition: background 0.1s ease;
}
.mm-pg-row:hover { background: #eff6ff; color: #1d4ed8; }
.mm-pg-row input[type="checkbox"] {
  width: 15px; height: 15px; accent-color: #3899ED; cursor: pointer;
  flex: 0 0 auto;
}
.mm-pg-icon { width: 18px; height: 18px; color: #6b7280; flex: 0 0 auto; }
.mm-pg-row:hover .mm-pg-icon { color: #1d4ed8; }
/* Fontello-class icon (matches LINK EDITOR's Internal Page tab) */
.mm-pg-icon-font {
  width: 18px; text-align: center; font-size: 16px; line-height: 1;
  color: #374151; flex: 0 0 auto;
  font-style: normal;
}
.mm-pg-row:hover .mm-pg-icon-font { color: #1d4ed8; }
.mm-pg-label { flex: 1 1 auto; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

`;
